import React, { useState } from "react";
import { Clock } from "lucide-react";
import { KIND_DEFAULTS } from "./recharts/ReMixCharts/hooks/useCriticalPeriods";
import "./CriticalPeriodsToggle.css";

interface Props {
  active: boolean;
  onToggle: (next: boolean) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  disabledReason?: string;
}

const TOOLTIP_KINDS = [
  { key: "solar",        label: "Solar window", hours: "10:00–16:00" },
  { key: "evening_ramp", label: "Evening ramp", hours: "17:00–21:00" },
  { key: "valley",       label: "Night valley", hours: "00:00–05:00" },
] as const;

export function CriticalPeriodsToggle({
  active,
  onToggle,
  label = "Critical Periods",
  className = "",
  disabled = false,
  disabledReason,
}: Props) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="cp-pill-wrapper">
      <button
        type="button"
        className={`cp-pill-toggle ${active && !disabled ? "is-on" : "is-off"} ${
          disabled ? "is-disabled" : ""
        } ${className}`}
        onClick={() => !disabled && onToggle(!active)}
        aria-pressed={active && !disabled}
        aria-disabled={disabled}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Clock size={13} strokeWidth={2} className="cp-pill-clock-icon" />
        <span className="cp-pill-label">
          {label}
          {disabled ? " (off)" : ""}
        </span>
        <span
          className={`cp-pill-switch ${active && !disabled ? "is-on" : "is-off"}`}
        >
          <span className="cp-pill-knob" />
        </span>
      </button>

      {showTooltip && (
        <div className="cp-pill-tooltip">
          <div className="cp-tip-heading">
            {disabled
              ? disabledReason ?? "Unavailable"
              : "Highlights critical time windows"}
          </div>
          <ul className="cp-tip-list">
            {TOOLTIP_KINDS.map(({ key, label: kLabel, hours }) => (
              <li key={key}>
                <span
                  className="cp-tip-dot"
                  style={{ background: KIND_DEFAULTS[key].color }}
                />
                <span className="cp-tip-icon">{KIND_DEFAULTS[key].icon}</span>
                <span className="cp-tip-name">{kLabel}</span>
                <span className="cp-tip-hours">{hours}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default CriticalPeriodsToggle;
